import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import { Alert, Button, Card, Field, Input } from '../components/ui';
import { localizeMessage, useLocale, useT } from '../i18n/locale';

interface ForgotResult {
  ok: true;
  message: string;
  messageCode?: string;
  /** Present only outside production, so the dev flow needs no mail server. */
  resetUrl?: string;
}

/**
 * Request a password reset link.
 *
 * Non-enumerating by design: the response is the same whether or not the
 * address is registered, so the success panel never confirms an account
 * exists. The reset link is emailed; in development it is shown here instead,
 * exactly like email verification.
 */
export default function ForgotPasswordScreen() {
  const t = useT();
  const { locale } = useLocale();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<{ message: string; devLink: string | null } | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<ForgotResult>('/forgot-password', { email });
      setSent({
        message: localizeMessage(locale, res.messageCode, res.message),
        devLink: res.resetUrl ?? null,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('forgot.failed'));
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="m-0 text-2xl font-black text-white">DR Inbound</h1>
          <p className="mt-1 text-sm text-white/60">{t('login.tagline')}</p>
        </div>

        <Card className="p-6">
          <h2 className="m-0 text-lg font-bold text-slate-800">{t('forgot.title')}</h2>

          {sent ? (
            <>
              <div className="mt-3">
                <Alert tone="success">{sent.message}</Alert>
              </div>
              {sent.devLink ? (
                <div className="mt-3 rounded-lg bg-amber-50 p-3 text-xs break-all text-amber-800">
                  <div className="mb-1 font-bold">{t('forgot.devNote')}</div>
                  <a href={sent.devLink} className="font-semibold text-amber-900 underline">
                    {sent.devLink}
                  </a>
                </div>
              ) : null}
              <div className="mt-5">
                <Link to="/login" className="no-underline">
                  <Button variant="secondary" className="w-full">
                    {t('forgot.backToLogin')}
                  </Button>
                </Link>
              </div>
            </>
          ) : (
            <form onSubmit={submit} className="mt-3 flex flex-col gap-3">
              <p className="m-0 text-sm text-slate-500">{t('forgot.intro')}</p>
              {error ? <Alert tone="error">{error}</Alert> : null}
              <Field label={t('common.email')}>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  autoComplete="username"
                  required
                />
              </Field>
              <Button type="submit" disabled={busy || !email.trim()}>
                {busy ? t('forgot.sending') : t('forgot.submit')}
              </Button>
              <Link
                to="/login"
                className="text-center text-sm font-semibold text-brand-500 no-underline hover:underline"
              >
                {t('forgot.backToLogin')}
              </Link>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
